import { useEffect } from 'react';

export const PUBLIC_SITE_URL = 'https://colinoki.com';

const DEFAULT_OG_IMAGE = '/favicon.ico';

const ensureMeta = (selector, createTag) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = createTag();
    document.head.appendChild(node);
  }
  return node;
};

const setMetaName = (name, content) => {
  const node = ensureMeta(`meta[name="${name}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', name);
    return meta;
  });
  node.setAttribute('content', content);
};

const setMetaProperty = (property, content) => {
  const node = ensureMeta(`meta[property="${property}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', property);
    return meta;
  });
  node.setAttribute('content', content);
};

const setCanonical = (href) => {
  const node = ensureMeta('link[rel="canonical"]', () => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    return link;
  });
  node.setAttribute('href', href);
};

export const resolveAbsoluteUrl = (path = '/') => {
  if (!path) return `${PUBLIC_SITE_URL}/`;
  if (path.startsWith('http')) return path;
  return `${PUBLIC_SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const setJsonLd = (id, data) => {
  if (!data) return;

  const scriptId = `cnk-jsonld-${id}`;
  const node = ensureMeta(`script#${scriptId}`, () => {
    const script = document.createElement('script');
    script.setAttribute('id', scriptId);
    script.setAttribute('type', 'application/ld+json');
    return script;
  });
  node.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const node = document.head.querySelector(`script#cnk-jsonld-${id}`);
  if (node) {
    node.remove();
  }
};

export const usePublicSeo = ({
  title,
  description,
  path = '/',
  type = 'website',
  image = DEFAULT_OG_IMAGE,
  robots = 'index,follow',
  structuredData = [],
}) => {
  useEffect(() => {
    const absoluteUrl = resolveAbsoluteUrl(path);
    const absoluteImage = image.startsWith('http') ? image : resolveAbsoluteUrl(image);

    document.title = title;
    setMetaName('description', description);
    setMetaName('robots', robots);

    setCanonical(absoluteUrl);

    setMetaProperty('og:type', type);
    setMetaProperty('og:title', title);
    setMetaProperty('og:description', description);
    setMetaProperty('og:url', absoluteUrl);
    setMetaProperty('og:image', absoluteImage);
    setMetaProperty('og:site_name', 'Coli Noki');
    setMetaProperty('og:locale', 'fr_FR');

    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);
    setMetaName('twitter:image', absoluteImage);

    structuredData.forEach((entry, index) => {
      const id = entry?.id || `page-${index}`;
      setJsonLd(id, entry?.data || entry);
    });

    return () => {
      structuredData.forEach((entry, index) => {
        removeJsonLd(entry?.id || `page-${index}`);
      });
    };
  }, [title, description, path, type, image, robots, structuredData]);
};
